import AddressSkeleton from 'components/Address/AddressSkeleton'
import PersonalFormSkeleton from 'containers/PersonalForm/PersonalFormSkeleton'
import PersonalSkeleton from 'containers/PersonalSkeleton'
import HouseholdSkeleton from 'containers/HouseholdSkeleton'
import PropertySkeleton from 'containers/PropertySkeleton'
import IncomeAllSkeleton from 'containers/IncomeAllSkeleton'
import ExpensesSkeleton from 'containers/ExpensesSkeleton'
import ActiveLoansSkeleton from 'components/ActiveLoans/ActiveLoansSkeleton'
import KfzFormSkeleton from 'containers/KfzForm/KfzFormSkeleton'
import PayoutAccountSkeleton from 'containers/PayoutAccountSkeleton'
import Typography from '@material-ui/core/Typography'
import { Unit } from 'components/Input/Text'
import { CivilStatus } from 'utils/formDependencies/statics/enums/CivilStatus'
import { DebtorRelationship } from 'utils/formDependencies/statics/enums/DebtorRelationship'
import EmploymentPositionSkeleton from 'containers/EmploymentPositionSkeleton'
import hasSecondDebtor from 'utils/formDependencies/conditions/hasSecondDebtor'
import InputSkeleton from 'components/SkeletonView/InputSkeleton'
import ReturningVisitorSkeleton from 'components/ReturningVisitor/ReturningVisitorSkeleton'
import { MINIMUM_VEHICLE_AMOUNT_VALUE } from 'formInitialValues'
import RootStore from '../stores/RootStore'
import AccountType from '../utils/formDependencies/statics/enums/AccountType'
import {
  reactionClearMortgageRateUsedProperty,
  reactionUpdateHasPreviousEmployment,
  reactionSetAmoutVehiclePriceInitialPayment
} from './utils/reactions'
import { CONFIG_SMAVA } from './utils/config'
import { LoadedConfig } from './config.interface'
import {
  LivingSituation,
  Vehicle
} from '../interfaces/ApolloResponse.interface'
import { Debtor } from '../containers/PersonalForm/PersonalForm.interface'

export const onLoad = ({ traversal }: RootStore) => {
  const {
    handleMultiplePathFormFieldChanges,
    data: { debtors }
  } = traversal

  if (debtors.relationship === DebtorRelationship.SPOUSE) {
    handleMultiplePathFormFieldChanges([
      {
        path: `debtors.primary.personal.civilStatus`,
        value: CivilStatus.MARRIED
      },
      {
        path: `debtors.secondary.personal.civilStatus`,
        value: CivilStatus.MARRIED
      }
    ])
  }

  return true
}

export default {
  name: CONFIG_SMAVA,
  version: '1.0',
  onLoad,
  pages: {
    base: {
      components: {
        headline: {
          component: Typography,
          props: {
            variant: 'h3',
            children: 'Mit smava Kredite von über 20 Partnern vergleichen'
          }
        },
        purpose: {
          lazy: () => import('../components/PathSelect'),
          props: {
            path: 'common.purpose'
          }
        },
        amount: {
          lazy: () => import('../components/Smava/PathAmountSelect'),
          props: {
            path: 'common.amount'
          }
        },
        kaufPries: {
          lazy: () => import('../components/PathAmountInput'),
          props: {
            path: 'common.vehicle.price',
            unit: Unit.EURO
          }
        },
        anzhalung: {
          lazy: () => import('../components/PathAmountInput'),
          props: {
            path: 'common.vehicle.initialPayment',
            unit: Unit.EURO
          }
        },
        term: {
          lazy: () => import('../components/PathSelect'),
          props: {
            path: 'common.term'
          }
        }
      }
    },
    contact: {
      components: {
        schufaBanner: {
          lazy: () => import('../components/Banner/SchufaBanner'),
          skeleton: ReturningVisitorSkeleton
        },
        loginBanner: {
          lazy: () => import('../components/Banner/LoginBanner'),
          skeleton: InputSkeleton,
          props: {
            dependson: 'isNotAffiliate'
          }
        },
        title: {
          lazy: () => import('../components/Smava/ContactTitle'),
          skeleton: InputSkeleton
        },
        purpose: {
          lazy: () => import('../components/PathSelect'),
          props: {
            path: 'common.purpose'
          },
          skeleton: InputSkeleton
        },
        amount: {
          lazy: () => import('../components/Smava/PathAmountSelect'),
          props: {
            path: 'common.amount'
          },
          skeleton: InputSkeleton
        },
        kaufPries: {
          lazy: () => import('../components/PathAmountInput'),
          props: {
            path: 'common.vehicle.price',
            unit: Unit.EURO
          },
          skeleton: InputSkeleton
        },
        anzhalung: {
          lazy: () => import('../components/PathAmountInput'),
          props: {
            path: 'common.vehicle.initialPayment',
            unit: Unit.EURO
          },
          skeleton: InputSkeleton
        },
        term: {
          lazy: () => import('../components/PathSelect'),
          props: {
            path: 'common.term',
            dependson: 'isNotMortgage'
          },
          skeleton: InputSkeleton
        },
        bestOfferBox: {
          lazy: () => import('../components/BestOfferBox'),
          props: {
            useSlimVariant: true
          }
        },
        personal_primary: {
          lazy: () => import('../containers/Smava/PersonalFormPrimary'),
          props: {
            debtor: Debtor.PRIMARY,
            dependson: 'isNotMortgage'
          },
          skeleton: PersonalFormSkeleton
        },
        debtorRelationship: {
          lazy: () => import('../components/DebtorRelationship'),
          props: {
            dependson: 'isNotMortgage'
          }
        },
        personal_secondary: {
          lazy: () => import('../containers/Smava/PersonalFormSecondary'),
          props: {
            debtor: Debtor.SECONDARY,
            dependson: 'isNotMortgage'
          },
          skeleton: PersonalFormSkeleton
        },
        agb: {
          lazy: () => import('../components/Smava/AGB'),
          props: {
            dependson: 'isNotMortgage'
          }
        }
      },
      reactions: ({ traversal }) => [
        {
          field: () => traversal.data.common.vehicle,
          method: (vehicle: Vehicle) => {
            const { price, initialPayment } = vehicle
            if (price < MINIMUM_VEHICLE_AMOUNT_VALUE) {
              traversal.setInitalPaymentErrorTriggered(false)
              return
            }
            const calculatedAmount = price - (initialPayment || 0)
            if (calculatedAmount < MINIMUM_VEHICLE_AMOUNT_VALUE) {
              traversal.setInitalPaymentErrorTriggered(true)
              reactionSetAmoutVehiclePriceInitialPayment(
                traversal,
                vehicle.price
              )
            }
          }
        }
      ]
    },
    address: {
      components: {
        personal_primary: {
          lazy: () => import('../containers/Smava/Personal.smava'),
          props: {
            debtor: Debtor.PRIMARY
          },
          skeleton: PersonalSkeleton
        },
        civilStatus_primary: {
          lazy: () => import('../components/Smava/CivilStatus'),
          props: {
            debtor: Debtor.PRIMARY
          }
        },
        personal_secondary: {
          lazy: () => import('../containers/Smava/Personal.smava'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY
          },
          skeleton: PersonalSkeleton
        },
        civilStatus_secondary: {
          lazy: () => import('../components/Smava/CivilStatus'),
          props: {
            debtor: Debtor.SECONDARY
          }
        },
        commonHousehold: {
          lazy: () => import('../containers/CommonHousehold')
        },
        address_primary: {
          lazy: () => import('../containers/Address'),
          props: {
            debtor: Debtor.PRIMARY
          },
          skeleton: AddressSkeleton
        },
        sameAddress: {
          lazy: () => import('../components/PathYesNo'),
          props: {
            path: 'debtors.isSameAddress'
          }
        },
        address_secondary: {
          lazy: () => import('../containers/Address'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY
          },
          skeleton: AddressSkeleton
        }
      }
    },
    household_employment: {
      components: {
        household_primary: {
          lazy: () => import('../containers/Smava/Household'),
          props: {
            debtor: Debtor.PRIMARY
          },
          skeleton: HouseholdSkeleton
        },
        household_secondary: {
          lazy: () => import('../containers/Smava/Household'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY
          },
          skeleton: HouseholdSkeleton
        },
        property_primary_self: {
          lazy: () => import('../containers/Smava/Property'),
          props: {
            debtor: Debtor.PRIMARY,
            self: true
          },
          skeleton: PropertySkeleton
        },
        property_secondary_self: {
          lazy: () => import('../containers/Smava/Property'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY,
            self: true
          },
          skeleton: PropertySkeleton
        },
        property_primary: {
          lazy: () => import('../containers/Smava/Property'),
          props: {
            debtor: Debtor.PRIMARY
          },
          skeleton: PropertySkeleton
        },
        property_secondary: {
          lazy: () => import('../containers/Smava/Property'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY
          },
          skeleton: PropertySkeleton
        },
        employment_position_primary: {
          lazy: () => import('../containers/EmploymentPosition'),
          props: {
            debtor: Debtor.PRIMARY
          },
          skeleton: EmploymentPositionSkeleton
        },
        pension_fund_primary: {
          lazy: () => import('../containers/Smava/PensionFund'),
          props: {
            debtor: Debtor.PRIMARY
          }
        },
        minijob_primary: {
          lazy: () => import('../components/PathYesNo'),
          props: {
            path: 'debtors.primary.hasMinijob',
            row: true
          }
        },
        employment_primary: {
          lazy: () => import('../containers/EmploymentFieldSet'),
          props: {
            debtor: Debtor.PRIMARY
          }
        },
        selfEmployment_primary: {
          lazy: () => import('../containers/SelfEmployment'),
          props: {
            debtor: Debtor.PRIMARY
          }
        },
        employment_position_secondary: {
          lazy: () => import('../containers/EmploymentPosition'),
          props: {
            debtor: Debtor.SECONDARY
          },
          skeleton: EmploymentPositionSkeleton
        },
        pensionFund_secondary: {
          lazy: () => import('../containers/Smava/PensionFund'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY
          }
        },
        minijob_secondary: {
          lazy: () => import('../components/PathYesNo'),
          props: {
            path: 'debtors.secondary.hasMinijob'
          },
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data)
        },
        employment_secondary: {
          lazy: () => import('../containers/EmploymentFieldSet'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY
          }
        },
        selfEmployment_secondary: {
          lazy: () => import('../containers/SelfEmployment'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY
          }
        }
      },
      reactions: ({ traversal }) => [
        {
          field: () => traversal.data.debtors.primary.household.livingSituation,
          method: (livingSituation: LivingSituation) =>
            reactionClearMortgageRateUsedProperty(
              traversal,
              livingSituation,
              Debtor.PRIMARY
            )
        },
        {
          field: () =>
            traversal.data.debtors.secondary.household.livingSituation,
          method: (livingSituation: LivingSituation) =>
            reactionClearMortgageRateUsedProperty(
              traversal,
              livingSituation,
              Debtor.SECONDARY
            )
        },
        {
          field: () =>
            traversal.data.debtors.primary.currentEmployment.since +
            traversal.data.debtors.primary.currentEmployment.position,
          method: () =>
            reactionUpdateHasPreviousEmployment(traversal, Debtor.PRIMARY)
        },
        {
          field: () =>
            traversal.data.debtors.secondary.currentEmployment.since +
            traversal.data.debtors.secondary.currentEmployment.position,
          method: () =>
            reactionUpdateHasPreviousEmployment(traversal, Debtor.SECONDARY)
        }
      ]
    },
    income_expenses: {
      components: {
        income_primary_main_source: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          props: {
            debtor: Debtor.PRIMARY,
            mainSource: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_secondary_main_source: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY,
            mainSource: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_primary_main_source_free_lancer: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          props: {
            debtor: Debtor.PRIMARY,
            showSelfEmployedIncome: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_secondary_main_source_free_lancer: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY,
            showSelfEmployedIncome: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_primary_secondary_source: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          props: {
            debtor: Debtor.PRIMARY,
            secondarySource: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_secondary_secondary_source: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY,
            secondarySource: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_primary_other: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          props: {
            debtor: Debtor.PRIMARY,
            otherSource: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_secondary_other: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY,
            otherSource: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_primary_mini_job: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          props: {
            debtor: Debtor.PRIMARY,
            miniJob: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_secondary_mini_job: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY,
            miniJob: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_primary_pensioner: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          props: {
            debtor: Debtor.PRIMARY,
            pensioner: true
          },
          skeleton: IncomeAllSkeleton
        },
        income_secondary_pensioner: {
          lazy: () => import('../containers/Smava/IncomeAllSmava'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY,
            pensioner: true
          },
          skeleton: IncomeAllSkeleton
        },
        expenses_primary: {
          lazy: () => import('../containers/Expenses'),
          props: {
            debtor: Debtor.PRIMARY
          },
          skeleton: ExpensesSkeleton
        },
        expenses_secondary: {
          lazy: () => import('../containers/Expenses'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY
          },
          skeleton: ExpensesSkeleton
        },
        activeLoans_primary: {
          lazy: () => import('../components/ActiveLoans'),
          props: {
            debtor: Debtor.PRIMARY
          },
          skeleton: ActiveLoansSkeleton(Debtor.PRIMARY)
        },
        activeLoans_secondary: {
          lazy: () => import('../components/ActiveLoans'),
          condition: ({ traversal: { data } }: RootStore) =>
            hasSecondDebtor(data),
          props: {
            debtor: Debtor.SECONDARY
          },
          skeleton: ActiveLoansSkeleton(Debtor.SECONDARY)
        }
      }
    },
    loanPayout: {
      components: {
        kfz: {
          condition: ({
            traversal: {
              data: { common }
            }
          }) =>
            common.purpose === 'PRE_OWNED_CAR' || common.purpose === 'NEW_CAR',
          lazy: () => import('../containers/KfzForm'),
          skeleton: KfzFormSkeleton
        },
        ratenRdi: {
          lazy: () => import('../containers/Smava/Rdi')
        },
        payoutAccount: {
          lazy: () => import('../containers/Smava/PayoutAccount'),
          skeleton: PayoutAccountSkeleton
        },
        dac: {
          lazy: () => import('../containers/DacContainer')
        }
      },
      reactions: ({ traversal }) => [
        {
          field: () => traversal.data.common.payoutAccount.type,
          method: (type) => {
            const pathPrefix = 'common.payoutAccount'
            const { handlePathFormFieldChange } = traversal

            if (type === AccountType.IBAN_BIC) {
              handlePathFormFieldChange(
                `${pathPrefix}.accountNumber`,
                '',
                false
              )
              handlePathFormFieldChange(`${pathPrefix}.bankCode`, '', false)
            } else {
              handlePathFormFieldChange(`${pathPrefix}.iban`, '', false)
            }
          }
        }
      ]
    }
  }
} as LoadedConfig
